import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import {ENV} from '../app/Utils/_helpers/EnvHelper'
import {
  AppRequest,
  AppResponse,
  BuyCreditParam,
  PaymentPricingRules,
  ContactRequest,
  ContactCountRequest,
  TagRequest,
  ListTagsResponse,
  ListUploadHistoryResponse,
  UpsertTag,
  ListCreditHistoryResponse,
  ListPurchaseHistoryResponse,
  ListDNPurchaseHistoryResponse,
  ContactClickResponse,
  Message,
  MessageReportParams,
  PaginateResponse,
  LineChartItem,
  ContactGroupMap,
  Tag,
  AllocateCreditParam,
  Sender,
} from './cellcast.v3.types'
import {GetCurrentData} from '../app/Utils/_helpers/GetCurrentData'
import {GetContactListResponse} from './cellcast.v3.types'
import axios from 'axios'
import {Group} from './cellcast.type'
import {toUpper} from 'lodash'
import {REGION_API_URLS} from '../app/Utils/Constant/ConstantForWebsite'
import {defaultRegion, regions} from '../_metronic/helpers'

const API_URL = ENV().REACT_APP_API_V3_URL

// Custom baseQuery function to dynamically retrieve the API_URL
const dynamicBaseQuery = async (args: any, api: any, extraOptions: any) => {
  // Retrieve the API_URL from local storage (or any other source)

  const currentUser = GetCurrentData()

  let region = currentUser?.region ? toUpper(currentUser?.region) : null
  let apiUrl =
    region !== defaultRegion.toUpperCase()
      ? (region && REGION_API_URLS[region]) || API_URL
      : API_URL

  if (args?.region && args?.region !== '') {
    const regionBaseUrl = REGION_API_URLS[args?.region.toUpperCase()]
    if (regionBaseUrl && regionBaseUrl !== '') {
      apiUrl = regionBaseUrl
      region = args?.region
    }
  }

  // Create a fetchBaseQuery instance with the dynamic API_URL
  const baseQuery = fetchBaseQuery({
    baseUrl: apiUrl, // Use the dynamically retrieved API_URL
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token')

      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      if (region) {
        headers.set('region', region)
      }

      return headers
    },
  })

  // Call the fetchBaseQuery instance with the original arguments
  return baseQuery(args, api, extraOptions)
}

export const cellcastV3Api = createApi({
  reducerPath: 'cellcast_api_v3',
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    pricingRules: builder.mutation<
      AppResponse<PaymentPricingRules[]>,
      AppRequest<{accountId: string}>
    >({
      query: (params) => ({
        url: 'billing',
        method: 'POST',
        body: params,
      }),
    }),
    buyCredit: builder.mutation<AppResponse<null>, AppRequest<BuyCreditParam>>({
      query: (params) => ({
        url: 'billing',
        method: 'POST',
        body: params,
      }),
    }),
    remainingBalance: builder.mutation<AppResponse<{sms: number; mms: number}>, AppRequest<null>>({
      query: (params) => ({
        url: 'billing',
        method: 'POST',
        body: params
      }),
    }),
    remainingBalanceRegion: builder.mutation<AppResponse<{sms: number; mms: number}>, { userId: string, region: string }>({
      query: ({ userId, region }) => ({
        url: 'billing',
        method: 'POST',
        body: {
          action: 'find_balance_by_user',
          data: {
            user_id: userId,
          }
        },
        region: region
      }),
    }),

    allocateCredit: builder.mutation<
      AppResponse<null>,
      {
        body: AppRequest<AllocateCreditParam>
        region: string
      }
    >({
      query: ({body, region}) => ({
        url: 'billing',
        method: 'POST',
        body: body,
        region: region,
      }),
    }),

    // contact section
    getContacts: builder.mutation<GetContactListResponse, AppRequest<ContactRequest>>({
      query: (query) => {
        return {
          url: 'contact',
          method: 'POST',
          body: query,
        }
      },
    }),

    fetchContacts: builder.query<AppResponse<PaginateResponse<ContactGroupMap>>, ContactRequest>({
      query: (query) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'find_contact',
          data: query,
        },
      }),
    }),

    getContactCount: builder.mutation<{data: number}, AppRequest<ContactCountRequest>>({
      query: (query) => {
        return {
          url: 'contact',
          method: 'POST',
          body: query,
        }
      },
    }),
    getTags: builder.mutation<AppResponse<ListTagsResponse>, AppRequest<TagRequest>>({
      query: (query) => {
        return {
          url: 'contact',
          method: 'POST',
          body: query,
        }
      },
    }),
    allTags: builder.mutation<AppResponse<Tag[]>, AppRequest<Omit<TagRequest, 'page' | 'limit'>>>({
      query: (query) => {
        return {
          url: 'contact',
          method: 'POST',
          body: query,
        }
      },
    }),
    fetchTag: builder.query({
      query: (query) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'find_tag',
          data: query,
        },
      }),
    }),
    deleteTag: builder.mutation<any, string[]>({
      query: (ids) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'delete_tag',
          data: {
            ids: ids,
          },
        },
      }),
    }),
    getUploadHistories: builder.query<AppResponse<ListUploadHistoryResponse>, any>({
      query: (query) => ({
        url: 'contact',
        method: 'POST',
        body: query,
      }),
    }),
    upsertTag: builder.mutation<
      AppResponse<{is_error: boolean; message: string}>,
      AppRequest<UpsertTag>
    >({
      query: (query) => ({
        url: 'contact',
        method: 'POST',
        body: query,
      }),
    }),
    getCreditHistory: builder.mutation<AppResponse<ListCreditHistoryResponse>, any>({
      query: (query) => ({
        url: 'billing',
        method: 'POST',
        body: query,
      }),
    }),
    getPurchaseHistory: builder.mutation<AppResponse<ListPurchaseHistoryResponse>, any>({
      query: (query) => ({
        url: 'billing',
        method: 'POST',
        body: query,
      }),
    }),

    calculateSummaryMessage: builder.mutation<AppResponse<null>, any>({
      query: (query) => ({
        url: 'bm',
        method: 'POST',
        body: query,
      }),
    }),
    sendMessage: builder.mutation<AppResponse<null>, any>({
      query: (query) => ({
        url: 'bm',
        method: 'POST',
        body: query,
      }),
    }),

    getBulkReportDetailMessage: builder.query({
      query: (query) => ({
        url: 'bm',
        method: 'POST',
        body: {
          action: 'report_message_paginate',
          data: query,
        },
      }),
    }),
    getDnPurchaseHistory: builder.mutation<AppResponse<ListDNPurchaseHistoryResponse>, any>({
      query: (query) => ({
        url: 'billing',
        method: 'POST',
        body: query,
      }),
    }),

    quickMessage: builder.mutation<AppResponse<null>, any>({
      query: (query) => ({
        url: 'qm',
        method: 'POST',
        body: query,
      }),
    }),

    quickMessageReports: builder.query<AppResponse<Message[]>, MessageReportParams>({
      query: (query) => ({
        url: 'qm',
        method: 'POST',
        body: {
          action: 'report_qm',
          data: query,
        },
      }),
    }),

    getReportLineCharts: builder.query<AppResponse<PaginateResponse<LineChartItem>>, any>({
      query: ({scope, type, ...query}) => ({
        url: 'report',
        method: 'POST',
        body: {
          action: 'get_report_line_chart',
          data: {
            ...query,
            scope,
            type,
          },
        },
      }),
    }),

    getReportListPaginateWithGroup: builder.query<
      AppResponse<PaginateResponse<LineChartItem>>,
      any
    >({
      query: (query) => ({
        url: 'report',
        method: 'POST',
        body: {
          action: 'get_report_list_paginate_with_group',
          data: query,
        },
      }),
    }),

    getReportList: builder.query<AppResponse<any>, any>({
      query: ({scope, type, ...query}) => ({
        url: 'report',
        method: 'POST',
        body: {
          action: 'get_report_list_paginate',
          data: {
            ...query,
            scope,
            type,
          },
        },
      }),
    }),

    updateContact: builder.mutation({
      query: (data) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'update_contact',
          data,
        },
      }),
    }),

    updateSubscriptionStatus: builder.mutation({
      query: (data) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'update_subscription_status',
          data,
        },
      }),
    }),

    deleteContacts: builder.mutation({
      query: (data) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'delete_contact',
          data,
        },
      }),
    }),

    getReportCampaign: builder.query<AppResponse<any>, any>({
      query: (query) => ({
        url: 'bm',
        method: 'POST',
        body: {
          action: 'report_bulk',
          data: query,
        },
      }),
    }),

    getTrackingLink: builder.query<AppResponse<ContactClickResponse>, any>({
      query: (query) => ({
        url: 'tracking-link',
        method: 'POST',
        body: query,
      }),
    }),

    updateContactTags: builder.mutation({
      query: (query) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'update_tags_contact',
          data: query,
        },
      }),
    }),

    fetchContactCount: builder.query({
      query: (query) => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'count_contact',
          data: query,
        },
      }),
    }),

    fetchReportPaginateListGroup: builder.query({
      query: (query) => ({
        url: 'report',
        method: 'POST',
        body: {
          action: 'get_report_list_paginate_with_group',
          data: query,
        },
      }),
    }),

    getCountWithGroup: builder.query({
      query: (query) => ({
        url: 'report',
        method: 'POST',
        body: {
          action: 'get_count_with_group',
          data: query,
        },
      }),
    }),

    getGroup: builder.query<AppResponse<Group>, any>({
      query: () => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'find_group',
        },
      }),
    }),

    getLatestUploadHistory: builder.query<AppResponse<Group>, any>({
      query: () => ({
        url: 'contact',
        method: 'POST',
        body: {
          action: 'find_latest_upload_history',
        },
      }),
    }),

    getUserSetting: builder.query<AppResponse<any>, any>({
      query: (params) => ({
        url: 'setting',
        method: 'POST',
        body: {
          action: 'find_global_setting',
          data: params,
        },
      }),
    }),

    getActiveSender: builder.query<AppResponse<any>, any>({
      query: () => ({
        url: 'sender',
        method: 'POST',
        body: {
          action: 'find_active_number',
        },
      }),
    }),

  }),
})

export const {
  // International Number
  usePricingRulesMutation,
  useBuyCreditMutation,
  useRemainingBalanceMutation,
  useRemainingBalanceRegionMutation,
  useGetContactsMutation,
  useGetContactCountMutation,
  useGetTagsMutation,
  useAllTagsMutation,
  useDeleteTagMutation,
  useGetUploadHistoriesQuery,
  useUpsertTagMutation,
  useGetCreditHistoryMutation,
  useGetPurchaseHistoryMutation,
  useCalculateSummaryMessageMutation,
  useSendMessageMutation,
  useGetDnPurchaseHistoryMutation,
  useQuickMessageMutation,
  useGetTrackingLinkQuery,
  useQuickMessageReportsQuery,
  useLazyQuickMessageReportsQuery,
  useGetReportLineChartsQuery,
  useGetReportListQuery,
  useGetReportCampaignQuery,
  useFetchContactsQuery,
  useUpdateContactMutation,
  useUpdateSubscriptionStatusMutation,
  useDeleteContactsMutation,
  useFetchTagQuery,
  useUpdateContactTagsMutation,
  useFetchContactCountQuery,
  useGetBulkReportDetailMessageQuery,
  useFetchReportPaginateListGroupQuery,
  useGetCountWithGroupQuery,
  useLazyGetGroupQuery,
  useLazyGetLatestUploadHistoryQuery,
  useLazyGetUserSettingQuery,
  useAllocateCreditMutation,
  useLazyGetActiveSenderQuery,
} = cellcastV3Api

export const fetchDataApiV3 = async (url: string, data: any, region?: string) => {
  const userRegion = GetCurrentData()

  if (!region) {
    region = userRegion?.region as string
  }


  const domain = REGION_API_URLS[region?.toUpperCase()]
  const response = await axios.post(`${domain}${url}`, data, {
    headers: {Authorization: `Bearer ${localStorage.getItem('token')}`, region: region},
  })

  return response.data
}
